import { omit } from 'lodash';

import { JOB_ERROR_CODE, JOB_STATUS } from 'src/data/job';
import { parseJson, promisedWait } from 'src/lib/common';
import { AbortError, JobError } from 'src/lib/errors';

import { queryJobResult } from './queryJobResult';
import { queryJobStatus } from './queryJobStatus';

const DEFAULT_POLLING_INTERVAL = 3000;

/**
 * Job options
 * @typedef JobWaitingOptions
 * @type {object}
 * @property {AbortSignal} abortSignal - tool group
 * @property {number} [pollingInterval] - list of tools
 * @property {function} [onProgress] - progress callback
 * @property {DocumentNode} [resultFragment] - GQL fragment for result
 */

/**
 * Wait for async job on BE to finish
 * @param {ID} id - ID of the job
 * @param {JobWaitingOptions} [options] - waiting options
 * @returns {Promise<any>} - job result
 */
export const waitForJob = async (id, options) => {
  if (options?.abortSignal?.aborted) {
    throw new AbortError();
  }

  // query job status
  const response = await queryJobStatus(id, { abortSignal: options?.abortSignal });
  const result = parseJson(response.result, null);

  if (response.status === JOB_STATUS.completed) {
    return result;
  }

  if (response.status === JOB_STATUS.failed) {
    const { message, code } = unwrapError(response);
    if (typeof options?.getErrorMessage === 'function') {
      const newMessage = options.getErrorMessage(code, message);
      if (newMessage) {
        throw new JobError(newMessage, code);
      }
    }
    throw new JobError(message, code);
  }

  // notify about job progress if requested,
  // wait if needed, cause outside logic might want to fetch something on this check cycle
  if (typeof options?.onProgress === 'function') {
    await options.onProgress({
      jobId: id,
      status: response.status,
      step: response.step,
      result,
    });
  }

  // wait some time and check again
  await promisedWait(options?.pollingInterval || DEFAULT_POLLING_INTERVAL);
  return waitForJob(id, options);
};

export const waitForJobWithEntityResult = async (id, gqlFragment, options) => {
  const jobOptions = options ? omit(options, 'list') : null;

  // if there is onProgress callback, we need to wrap it to fetch "partial" results on each "step" change
  if (typeof jobOptions?.onProgress === 'function') {
    const originalOnProgress = jobOptions.onProgress;

    let progressStep;
    jobOptions.onProgress = async (data) => {
      if (data.step == null) {
        return;
      }

      if (progressStep !== data.step) {
        progressStep = data.step;
        const entityResult = await queryJobResult(id, gqlFragment, { list: options?.list });
        originalOnProgress({
          ...data,
          entityResult,
        });
      }
    };
  }

  // wait for job to finish
  await waitForJob(id, jobOptions);

  // fetch final result
  return queryJobResult(id, gqlFragment, { list: options?.list });
};

const unwrapError = (response) => {
  const code = response.processingErrors?.[0]?.code || JOB_ERROR_CODE.generalError;
  const message = response.processingErrors?.[0]?.message;
  return {
    code,
    message,
  };
};
